import React, { useState, useEffect } from 'react';
import { Modal, Button, MarkdownRenderer } from '../common';
import { calculatePercentOriginal, formatPercentOriginal, isEdited } from '../../utils/textSimilarity';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    return `${API_URL}${url}`;
  }
  return url;
};

/**
 * Block item for the expanded passage editor
 * Player prose and GM text content are editable inline, media is display-only
 */
const EditableBlockItem = ({
  block,
  index,
  isFirst,
  isLast,
  editedContent,
  originalContent,
  onContentChange,
  onRevert,
  onMoveUp,
  onMoveDown,
  onRemove
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const isTextGMBlock = block.type === 'gm_prose' || block.type === 'gm_transition' || block.type === 'gm_text';
  const isPlayerProse = block.type === 'player_content' && block.contentType === 'prose';
  const isEditableText = isTextGMBlock || isPlayerProse;
  const isMediaGMBlock = block.type === 'gm_image' || block.type === 'gm_audio';
  const isTransition = block.type === 'gm_transition';
  const hasChanges = isEditableText && editedContent !== originalContent;

  // For player content, check if edited from submission original
  const playerOriginal = isPlayerProse ? (block.originalContent || originalContent) : null;
  const playerHasBeenEdited = isPlayerProse && playerOriginal && isEdited(playerOriginal, editedContent);
  const percentOriginal = playerHasBeenEdited ? calculatePercentOriginal(playerOriginal, editedContent) : null;

  const getBlockLabel = () => {
    switch (block.type) {
      case 'player_content':
        return block.contentType === 'prose' ? 'Player Text' :
               block.contentType === 'image' ? 'Player Image' : 'Player Audio';
      case 'gm_prose':
        return 'GM Prose';
      case 'gm_transition':
        return 'GM Transition';
      case 'gm_text':
        return 'GM Text';
      case 'gm_image':
        return 'GM Image';
      case 'gm_audio':
        return 'GM Audio';
      default:
        return 'Content';
    }
  };

  const getBlockColor = () => {
    switch (block.type) {
      case 'player_content':
        return 'border-l-blue-500 bg-blue-50';
      case 'gm_prose':
      case 'gm_transition':
      case 'gm_text':
        return 'border-l-purple-500 bg-purple-50';
      case 'gm_image':
      case 'gm_audio':
        return 'border-l-purple-400 bg-purple-50';
      default:
        return 'border-l-gray-300';
    }
  };

  return (
    <div className={`border-l-4 p-4 rounded-r mb-3 ${getBlockColor()}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-gray-500 uppercase">
            {index + 1}. {getBlockLabel()}
          </span>
          {hasChanges && !isPlayerProse && (
            <span className="text-xs text-amber-600 font-medium">(modified)</span>
          )}
          {playerHasBeenEdited && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
              {formatPercentOriginal(percentOriginal) || 'edited'}
            </span>
          )}
        </div>

        {/* Move and remove buttons */}
        <div className="flex items-center gap-1">
          <button
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded disabled:opacity-30"
            onClick={onMoveUp}
            disabled={isFirst}
            title="Move up"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded disabled:opacity-30"
            onClick={onMoveDown}
            disabled={isLast}
            title="Move down"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-white/50 rounded"
            onClick={onRemove}
            title="Remove"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      {isEditableText ? (
        /* Editable text content - GM text or player prose */
        <div className="space-y-2">
          {isFocused ? (
            <textarea
              className="w-full border rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
              rows={5}
              placeholder="Write your text..."
              value={editedContent}
              onChange={(e) => onContentChange(e.target.value)}
              onBlur={() => setIsFocused(false)}
              autoFocus
            />
          ) : (
            <div
              className={`w-full border rounded-lg p-3 text-sm bg-white cursor-text min-h-[124px] ${isTransition ? 'italic text-gray-600' : ''}`}
              onClick={() => setIsFocused(true)}
            >
              {editedContent ? (
                <MarkdownRenderer content={editedContent} />
              ) : (
                <span className="text-gray-400">
                  Write your text...
                </span>
              )}
            </div>
          )}
          {(hasChanges || playerHasBeenEdited) && (
            <div className="flex justify-end">
              <Button
                variant="link"
                size="sm"
                onClick={onRevert}
              >
                Revert changes
              </Button>
            </div>
          )}
        </div>
      ) : isMediaGMBlock ? (
        /* GM media content - display only */
        <>
          {block.type === 'gm_image' && block.mediaUrl && (
            <img
              src={getMediaUrl(block.mediaUrl)}
              alt="GM image"
              className="h-24 w-auto rounded"
            />
          )}
          {block.type === 'gm_audio' && block.mediaUrl && (
            <audio controls className="h-8">
              <source src={getMediaUrl(block.mediaUrl)} />
            </audio>
          )}
        </>
      ) : (
        /* Player media content - read-only */
        <>
          {block.contentType === 'image' ? (
            <div>
              {block.mediaUrl && (
                <img
                  src={getMediaUrl(block.mediaUrl)}
                  alt={block.content}
                  className="h-24 w-auto rounded"
                />
              )}
            </div>
          ) : block.contentType === 'audio' ? (
            <div>
              {block.mediaUrl && (
                <audio controls className="h-8">
                  <source src={getMediaUrl(block.mediaUrl)} />
                </audio>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

/**
 * Full-screen modal for editing the passage with inline content editing
 */
export const PassageEditorModal = ({
  isOpen,
  onClose,
  blocks,
  onSave,
  onAddGMText,
  onAddGMImage,
  onAddGMAudio,
  uploading
}) => {
  // Track the working state of blocks (with position changes) and edited content
  const [workingBlocks, setWorkingBlocks] = useState([]);
  const [editedContents, setEditedContents] = useState({});
  const [originalContents, setOriginalContents] = useState({});

  // Initialize state when modal opens
  useEffect(() => {
    if (isOpen) {
      setWorkingBlocks([...blocks]);
      // Store original content for each editable text block
      const originals = {};
      blocks.forEach(block => {
        const isTextGMBlock = block.type === 'gm_prose' || block.type === 'gm_transition' || block.type === 'gm_text';
        const isPlayerProse = block.type === 'player_content' && block.contentType === 'prose';
        if (isTextGMBlock || isPlayerProse) {
          originals[block.id] = block.content || '';
        }
      });
      setOriginalContents(originals);
      setEditedContents(originals);
    }
  }, [isOpen, blocks]);

  const handleContentChange = (blockId, content) => {
    setEditedContents(prev => ({
      ...prev,
      [blockId]: content
    }));
  };

  const handleRevert = (blockId) => {
    const block = workingBlocks.find(b => b.id === blockId);
    // For player prose, revert to originalContent (from submission) if available
    if (block?.type === 'player_content' && block?.originalContent) {
      setEditedContents(prev => ({
        ...prev,
        [blockId]: block.originalContent
      }));
    } else {
      setEditedContents(prev => ({
        ...prev,
        [blockId]: originalContents[blockId]
      }));
    }
  };

  const handleMoveBlock = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= workingBlocks.length) return;
    const newBlocks = [...workingBlocks];
    const [moved] = newBlocks.splice(fromIndex, 1);
    newBlocks.splice(toIndex, 0, moved);
    setWorkingBlocks(newBlocks);
  };

  const handleRemoveBlock = (index) => {
    const newBlocks = workingBlocks.filter((_, i) => i !== index);
    setWorkingBlocks(newBlocks);
  };

  const handleAddText = () => {
    const newBlock = {
      type: 'gm_text',
      content: '',
      contentType: 'prose',
      id: `block-${Date.now()}`
    };
    setWorkingBlocks([...workingBlocks, newBlock]);
    setOriginalContents(prev => ({ ...prev, [newBlock.id]: '' }));
    setEditedContents(prev => ({ ...prev, [newBlock.id]: '' }));
  };

  const handleSave = () => {
    // Apply edited contents to working blocks
    const finalBlocks = workingBlocks.map(block => {
      const isTextGMBlock = block.type === 'gm_prose' || block.type === 'gm_transition' || block.type === 'gm_text';
      const isPlayerProse = block.type === 'player_content' && block.contentType === 'prose';

      if (isTextGMBlock) {
        return {
          ...block,
          content: editedContents[block.id] || ''
        };
      }

      if (isPlayerProse) {
        const newContent = editedContents[block.id] || '';
        // Set originalContent if editing for first time
        const originalContent = block.originalContent || originalContents[block.id];
        return {
          ...block,
          content: newContent,
          originalContent: originalContent
        };
      }

      return block;
    });
    onSave(finalBlocks);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Check if there are any changes
  const hasChanges = () => {
    // Check for content changes
    for (const blockId in editedContents) {
      if (editedContents[blockId] !== originalContents[blockId]) {
        return true;
      }
    }
    // Check for structural changes (different number of blocks or different order)
    if (workingBlocks.length !== blocks.length) return true;
    for (let i = 0; i < workingBlocks.length; i++) {
      if (workingBlocks[i].id !== blocks[i].id) return true;
    }
    return false;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Edit Passage"
      size="xl"
    >
      <div className="space-y-4">
        {/* Instructions */}
        <p className="text-sm text-gray-600">
          Reorder blocks using the arrow buttons. Edit GM text and player prose directly below.
          Media content is read-only.
        </p>

        {/* Blocks */}
        <div className="max-h-[50vh] overflow-y-auto pr-2">
          {workingBlocks.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
              <p>No content blocks yet.</p>
              <p className="text-sm mt-1">Add GM content below.</p>
            </div>
          ) : (
            workingBlocks.map((block, index) => (
              <EditableBlockItem
                key={block.id || index}
                block={block}
                index={index}
                isFirst={index === 0}
                isLast={index === workingBlocks.length - 1}
                editedContent={editedContents[block.id] ?? block.content}
                originalContent={originalContents[block.id] ?? block.content}
                onContentChange={(content) => handleContentChange(block.id, content)}
                onRevert={() => handleRevert(block.id)}
                onMoveUp={() => handleMoveBlock(index, index - 1)}
                onMoveDown={() => handleMoveBlock(index, index + 1)}
                onRemove={() => handleRemoveBlock(index)}
              />
            ))
          )}
        </div>

        {/* Add buttons */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAddText}
          >
            + Add Text
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // Save current state before opening file picker
              handleSave();
              onAddGMImage?.();
            }}
            disabled={uploading}
            loading={uploading}
          >
            + Add Image
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // Save current state before opening file picker
              handleSave();
              onAddGMAudio?.();
            }}
            disabled={uploading}
            loading={uploading}
          >
            + Add Audio
          </Button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-gray-500">
            {workingBlocks.length} block{workingBlocks.length !== 1 ? 's' : ''}
            {hasChanges() && <span className="text-amber-600 ml-2">(unsaved changes)</span>}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Changes & Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
